import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import * as React from "react";
import { useNavigate } from "react-router";
import { useUserAuth } from "../contexts/authContext";
import { ErrorComponent } from "../errorComponent";
import { PASSWORD_ERROR, IsInvalidEmail, IsInvalidPassword } from "./userUtil";

export function CreateUser() {
    const [emailErrorMessage, setEmailErrorMessage] = React.useState("");
    const [passwordErrorMessage, setPasswordErrorMessage] = React.useState("");
    const [organization, setOrganization] = React.useState("");
    const [requestDisabled, setRequestDisabled] = React.useState(false);
    const inputStyle = { WebkitBoxShadow: "0 0 0 1000px #121212 inset" };
    const isMobile = useMediaQuery("(max-width:400px)");
    const { organizations, statusMessage, setStatusMessage } = useUserAuth();
    const navigateTo = useNavigate();

    React.useEffect(() => {
        setStatusMessage("");
    }, []);

    const handleSubmit = async (e) => {
        try {
            e.preventDefault();
            const data = new FormData(e.currentTarget);

            // build request
            const updatedUser = {
                email: data.get("email").toLowerCase(),
                password: data.get("password"),
                organization_id: data.get("organization"),
                oidc: false
            };
            // validate email format
            if (IsInvalidEmail(updatedUser.email)) {
                return setEmailErrorMessage("The entered email is invalid");
            } else if (emailErrorMessage) {
                setEmailErrorMessage("");
            }
            // validate password format
            if (IsInvalidPassword(updatedUser.password)) {
                return setPasswordErrorMessage(PASSWORD_ERROR);
            } else if (passwordErrorMessage) {
                setPasswordErrorMessage("");
            }
            // make request
            const response = await fetch("api/user/create", {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(updatedUser)
            });
            const responseData = await response.json();

            // handle the different responses
            if (response.status === 200) {
                setStatusMessage(responseData.message);
                setRequestDisabled(true);
            } else if (response.status === 409) {
                setEmailErrorMessage(responseData.message);
            } else if (response.status === 500) {
                setStatusMessage(responseData.message);
            }
        } catch (err) {
            setStatusMessage("Error: Please try again later");
            console.error(err);
        }
    };
    const handleCancel = () => {
        navigateTo("/login");
    };
    return (
        <Box sx={{ display: "flex", flexDirection: "column", py: 6, m: "auto", maxWidth: "400px", height: "100%", overflow: "auto", alignItems: "center" }}>
            <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
                <LockOutlinedIcon />
            </Avatar>
            <Typography component="h1" variant="h5">
                Request Account
            </Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ m: 4 }}>
                <TextField
                    error={emailErrorMessage.length > 0 ? true : false}
                    helperText={emailErrorMessage}
                    inputProps={{ style: inputStyle, "data-testid": "email" }}
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    autoFocus
                />
                <TextField
                    error={passwordErrorMessage.length > 0 ? true : false}
                    helperText={passwordErrorMessage}
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    type="password"
                    id="password"
                    autoComplete="current-password"
                    inputProps={{ style: inputStyle, "data-testid": "password" }}
                />
                <FormControl required fullWidth sx={{ mt: 2 }}>
                    <InputLabel id="organization-label">Organization</InputLabel>
                    <Select
                        inputProps={{ "data-testid": "organization" }}
                        name="organization"
                        labelId="organization-label"
                        id="selectOrganization"
                        data-testid="selectOrganization"
                        value={organization}
                        label="Organization"
                        onChange={(e) => setOrganization(e.target.value)}
                    >
                        {organizations
                            .filter((org) => !org.decommissioned)
                            .map((organization) => (
                                <MenuItem key={organization.id} value={organization.id} id={organization.id}>
                                    {organization.name}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>
                <ErrorComponent statusMessage={statusMessage} />
                <Box sx={{ justifyContent: "space-between", alignItems: "center", display: "flex" }}>
                    <Button onClick={handleCancel} variant="contained" sx={{ mt: 3, mb: 2 }} size={isMobile ? "small" : "medium"}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        sx={{ mt: 3, mb: 2 }}
                        disabled={requestDisabled}
                        size={isMobile ? "small" : "medium"}
                        id="requestAccount"
                    >
                        Request New Account
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
